import {
  fetchMFAPreference,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
  type SignInOutput,
} from 'aws-amplify/auth'
import { isCognitoConfigured } from '../config/cognito'
import { normalizeTotpCode } from '../features/security/totp-code'

/**
 * Cognito software-token (TOTP) MFA — the authenticator-app second factor.
 *
 * Adapter boundary: components and hooks call this module, never
 * `aws-amplify/auth` directly. Sign-in challenge handling lives in
 * auth-service.ts alongside the other challenge responses; this module owns
 * enrolment and preference only.
 *
 * The pool is MFA=OPTIONAL, so a user with no verified authenticator is never
 * challenged. Enrolling is what turns the factor on for that one account.
 */

/** Shown as the account group in authenticator apps (Google, 1Password, …). */
export const AUTHENTICATOR_ISSUER = 'Ascend Messenger'

export type TotpStatus = {
  /** A verified authenticator is registered, so sign-in will challenge. */
  enrolled: boolean
  /** TOTP is the preferred factor. Set together with enrolment. */
  preferred: boolean
}

export type TotpEnrolmentSecret = {
  /** Base32 secret for manual entry when a QR code cannot be scanned. */
  secret: string
  /** `otpauth://totp/...` — QR payload, and a deep link on mobile. */
  otpauthUri: string
}

function assertConfigured() {
  if (!isCognitoConfigured()) {
    throw new Error('Cognito is not configured. Set VITE_COGNITO_* in frontend/.env.local')
  }
}

export async function fetchTotpStatus(): Promise<TotpStatus> {
  assertConfigured()
  const preference = await fetchMFAPreference()
  return {
    enrolled: Boolean(preference.enabled?.includes('TOTP')),
    preferred: preference.preferred === 'TOTP',
  }
}

/**
 * Ask Cognito for a fresh shared secret. Safe to call for a user who already
 * has an authenticator: the new secret only replaces the old one once
 * `completeTotpEnrolment` verifies it, which is how "I got a new phone" works
 * without an admin in the loop.
 */
export async function beginTotpEnrolment(accountLabel: string): Promise<TotpEnrolmentSecret> {
  assertConfigured()
  const details = await setUpTOTP()
  return {
    secret: details.sharedSecret,
    otpauthUri: details.getSetupUri(AUTHENTICATOR_ISSUER, accountLabel).toString(),
  }
}

/**
 * Verify the first generated code and make TOTP the preferred factor. Cognito
 * only adds SOFTWARE_TOKEN_MFA to the user's MFA settings after a successful
 * verification, so an abandoned setup leaves the account exactly as it was.
 */
export async function completeTotpEnrolment(code: string): Promise<void> {
  assertConfigured()
  await verifyTOTPSetup({ code: normalizeTotpCode(code) })
  await updateMFAPreference({ totp: 'PREFERRED' })
}

/**
 * Read the secret Cognito hands back with a `CONTINUE_SIGN_IN_WITH_TOTP_SETUP`
 * challenge, in app shape, so the sign-in page never touches Amplify types.
 *
 * That challenge cannot occur while the pool is MFA=OPTIONAL — Cognito only
 * issues it when MFA is required and the account has no authenticator. It is
 * handled now so that flipping the pool to REQUIRED is a configuration change
 * on its own rather than one that also needs a client release.
 */
export function readTotpSetupFromSignIn(
  output: SignInOutput,
  accountLabel: string,
): TotpEnrolmentSecret | null {
  const step = output.nextStep
  if (step?.signInStep !== 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') return null
  return {
    secret: step.totpSetupDetails.sharedSecret,
    otpauthUri: step.totpSetupDetails.getSetupUri(AUTHENTICATOR_ISSUER, accountLabel).toString(),
  }
}
